export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  otpRequired?: boolean;
  success?: boolean;
  otpSent?: boolean;
  user?: User;
}

export interface User {
  _id: string;
  email: string;
  mobileNumber?: string;
  firstName: {
    en?: string;
    ar?: string;
  };
  lastName: {
    en?: string;
    ar?: string;
  };
  translations?: {
    en?: string;
    ar?: string;
  };
  roles: string[];
  permissions: string[];
  claims?: string[];
  schools?: string[];
  canteens?: string[];
  merchants?: string[];
  isProfileSetup: boolean;
  payByFaceEnabled?: boolean;
  payByFaceTNCAccepted?: boolean;
  payByQRCodeEnabled?: boolean;
  paymentQRCodePath?: string;
  activeCoupon?: any;
  cardOrderFeature?: boolean;
  orderStatus?: string;
  hasFacePay?: boolean;
  toShowFacePay?: boolean;
  allowTeacherToDismissFromScreenEnabled?: boolean;
  // ─── BE-288 portal scope claims (read defensively; BE field names TBD) ───
  branchScope?: 'all' | 'specific';
  branchIds?: string[];
  serviceIds?: string[];
  modulePermissions?:
    | Record<string, string[]>
    | { moduleId: string; view?: boolean; create?: boolean; edit?: boolean; delete?: boolean }[];
  /**
   * Actual BE-288 runtime shape emitted in the access token: granted module
   * keys (UPPERCASE PORTAL_MODULES catalog keys) and, per module, the granted
   * page constants. The /users/profile RTO does NOT carry this — it's decoded
   * from the JWT and merged onto the user (see AuthService.decodeTokenClaims).
   */
  portalAccess?: {
    modules?: string[];
    pages?: Record<string, string[]>;
    /** FE-295: third level, e.g. `{ INVENTORY: { CATALOGUE: ['PHYSICAL'] } }`. */
    subPages?: Record<string, Record<string, string[]>>;
  } | null;
  businessIds?: string[];
  serviceNames?: string[];
  /** Account type from /users/profile — drives the portal login gate. */
  userType?: AccountUserType | string;
  /** Business profiles attached to the account (empty/absent for app-only users). */
  BusinessUserProfile?: BusinessUserProfileSummary[];
}

export interface OtpRequest {
  userId: string;
  otpCode: string;
}

export interface OtpVerificationResponse {
  success: boolean;
  message?: string;
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  otpRequired?: boolean;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * FE-204 password reset. The auth service has no reset-token endpoint — the
 * flow is an OTP of type PASSWORD_RESET, optionally checked with
 * /auth/otp/validate (which does not consume it) and then spent by
 * /auth/otp/reset-password.
 */
export type OtpPurpose = 'LOGIN' | 'PASSWORD_RESET' | 'VERIFICATION' | 'PHONE_VERIFICATION' | 'EMAIL_VERIFICATION';

export interface OtpRequestPayload {
  email: string;
  deliveryMethod: 'email' | 'sms';
  type: OtpPurpose;
}

export interface ValidateOtpPayload {
  email: string;
  otpCode: string;
  type: Extract<OtpPurpose, 'LOGIN' | 'PASSWORD_RESET'>;
}

/** ValidateOtpRto — a rejected code normally 400s, but `valid` is read too. */
export interface ValidateOtpResponse {
  valid?: boolean;
  message?: string;
}

export interface ResetPasswordPayload {
  email: string;
  otpCode: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  success?: boolean;
  message?: string;
}

/**
 * `userType` on the /users/profile RTO (auth spec: UserProfileRto). PORTAL_USER
 * and SUPER_ADMIN are not in the published enum but are accepted defensively —
 * the BE may start emitting them and they are portal-eligible either way.
 */
export type AccountUserType =
  | 'BUSINESS_USER'
  | 'ADMIN_USER'
  | 'ADULT_USER'
  | 'DEPENDENT_USER'
  | 'PORTAL_USER'
  | 'SUPER_ADMIN';

/** BusinessUserProfileSummaryRto — the business profiles attached to the account. */
export interface BusinessUserProfileSummary {
  fullNameEn: string;
  fullNameAr: string;
  userType: 'APP_USER' | 'PORTAL_USER';
}

/**
 * Account types allowed straight into the portal. An ADULT_USER is deliberately
 * excluded here: it only gets in when it also carries a business profile.
 */
export const PORTAL_ALLOWED_USER_TYPES: readonly AccountUserType[] = [
  'ADMIN_USER',
  'PORTAL_USER',
  'SUPER_ADMIN',
  'BUSINESS_USER'
];

/**
 * Portal login gate, applied to the /users/profile payload right after the OTP
 * (or no-OTP login) hands us tokens. App-only accounts — an ADULT_USER parent
 * with no business profile, a DEPENDENT_USER — must not reach the portal.
 */
export function isPortalLoginAllowed(profile: Pick<User, 'userType' | 'BusinessUserProfile'> | null | undefined): boolean {
  const userType = profile?.userType as AccountUserType | undefined;
  if (!userType) return false;
  if (PORTAL_ALLOWED_USER_TYPES.includes(userType)) return true;
  if (userType === 'ADULT_USER') {
    return (profile?.BusinessUserProfile?.length ?? 0) > 0;
  }
  return false;
}

/** Thrown when the profile behind a freshly issued token is not portal-eligible. */
export class PortalAccessDeniedError extends Error {
  /** Dictionary key for the default message; screens translate it with `message` as the fallback. */
  readonly i18nKey = 'auth.err.portalAccessDenied';

  constructor(message = 'This account does not have access to the WAKI portal.') {
    super(message);
    this.name = 'PortalAccessDeniedError';
    Object.setPrototypeOf(this, PortalAccessDeniedError.prototype);
  }
}
